import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DiscountForm from "@/components/DiscountForm";

const empty = { code: "", type: "percent", value: "", active: true, min_order: "", expires_at: "", max_uses: "", product_ids: [], category_ids: [], applies_to_shipping: false };
const toPayload = (f) => ({
  code: f.code, type: f.type, value: parseFloat(f.value), active: f.active, applies_to_shipping: !!f.applies_to_shipping,
  min_order: f.min_order === "" || f.min_order == null ? 0 : parseFloat(f.min_order), expires_at: f.expires_at || "",
  max_uses: f.max_uses === "" || f.max_uses == null ? null : parseInt(f.max_uses, 10),
  product_ids: f.product_ids || [], category_ids: f.category_ids || [],
});

export default function Discounts() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  const load = async () => setList((await api.get("/admin/discounts")).data);
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => !p.archived)));
    api.get("/admin/categories").then((r) => setCats(r.data));
  }, []);

  const openOrders = async (d) => {
    const { data } = await api.get(`/admin/discounts/${d.id}/orders`);
    setDetail(data);
  };
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...empty, ...d, min_order: d.min_order || "", max_uses: d.max_uses ?? "", expires_at: d.expires_at || "" }); setEditId(d.id); setOpen(true); };

  const save = async () => {
    if (!form.code || form.value === "") return toast.error("Code and value are required");
    try {
      if (editId) await api.put(`/admin/discounts/${editId}`, toPayload(form));
      else await api.post("/admin/discounts", toPayload(form));
      toast.success(editId ? "Discount code updated" : "Discount code created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (d) => { try { await api.put(`/admin/discounts/${d.id}`, toPayload({ ...d, active: !d.active })); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (d) => { if (!window.confirm(`Delete code ${d.code}? Past orders keep their discount.`)) return; await api.delete(`/admin/discounts/${d.id}`); load(); };
  const scope = (d) => (d.product_ids?.length || d.category_ids?.length) ? `${d.category_ids?.length || 0} cat · ${d.product_ids?.length || 0} prod` : "All products";
  const expired = (d) => d.expires_at && d.expires_at < new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

  return (
    <div data-testid="discounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Discount Codes</h1>
          <p className="mt-1 text-sm text-muted-foreground">Codes customers can apply at checkout</p>
        </div>
        <Button onClick={openNew} data-testid="add-discount-btn" className="rounded-lg bg-primary font-semibold text-primary-foreground hover:bg-[#7CBCE0]"><Plus className="mr-1.5 h-4 w-4" /> Add Code</Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="overline">Code</TableHead>
            <TableHead className="overline">Value</TableHead>
            <TableHead className="overline">Rules</TableHead>
            <TableHead className="overline">Used</TableHead>
            <TableHead className="overline">Collected</TableHead>
            <TableHead className="overline">Active</TableHead>
            <TableHead className="overline text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No discount codes yet.</TableCell></TableRow>}
            {list.map((d) => (
              <TableRow key={d.id} className="hover:bg-slate-50" data-testid={`discount-row-${d.id}`}>
                <TableCell>
                  <button onClick={() => openOrders(d)} data-testid={`view-discount-orders-${d.id}`} className="font-mono font-semibold text-primary-foreground/90 underline decoration-primary decoration-2 underline-offset-4 hover:text-black">{d.code}</button>
                  {expired(d) && <span className="ml-2 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">EXPIRED</span>}
                </TableCell>
                <TableCell>{d.type === "percent" ? `${d.value}%` : money(d.value)}{d.applies_to_shipping && <span className="ml-1 text-xs text-muted-foreground">+ shipping</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  <div>{scope(d)}</div>
                  <div>{d.min_order > 0 ? `Min ${money(d.min_order)}` : "No minimum"}{d.expires_at ? ` · until ${d.expires_at}` : ""}</div>
                </TableCell>
                <TableCell data-testid={`discount-usage-${d.id}`}>{d.usage_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</TableCell>
                <TableCell className="font-semibold">{money(d.total_amount)}</TableCell>
                <TableCell><Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`toggle-discount-${d.id}`} /></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-discount-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`delete-discount-${d.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="discount-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Discount Code" : "Add Discount Code"}</DialogTitle></DialogHeader>
          <DiscountForm form={form} setForm={setForm} products={products} cats={cats} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-discount-btn" className="bg-primary text-primary-foreground hover:bg-[#7CBCE0]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="discount-orders-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Orders using <span className="font-mono">{detail.code}</span></DialogTitle></DialogHeader>
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="overline">Order</TableHead>
                    <TableHead className="overline">Customer</TableHead>
                    <TableHead className="overline">Status</TableHead>
                    <TableHead className="overline text-right">Discount</TableHead>
                    <TableHead className="overline text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {detail.orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No orders have used this code yet.</TableCell></TableRow>}
                    {detail.orders.map((o) => (
                      <TableRow key={o.id} data-testid={`discount-order-${o.id}`}>
                        <TableCell className="font-semibold">{o.order_number}</TableCell>
                        <TableCell>{o.customer?.name || "—"}</TableCell>
                        <TableCell><StatusBadge status={o.status} /></TableCell>
                        <TableCell className="text-right text-emerald-600">-{money(o.discount_amount)}</TableCell>
                        <TableCell className="text-right font-medium">{money(o.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary/60 px-4 py-3">
                <span className="text-sm font-medium">{detail.order_count} order(s) · total discount given {money(detail.total_discount)}</span>
                <span className="font-head text-lg font-extrabold" data-testid="discount-total-amount">{money(detail.total_amount)}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
